import React, { useState, useMemo, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import SelectList from "react-select";
import {
  RecordType,
  selectClassName,
  selectStyles,
  OTISRecords,
  getInputClassName,
  TableRowProps,
  HIDDEN_GRID_COLUMNS,
} from "../OtisHelper";
import { setInputtedData, setFinalData, setIsScroll } from "../../../redux/reducers/OTISReducer";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../redux/store";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import { user } from "../../../redux/reducers/UserReducer";
import { formatNumber } from "../../../utils/formatNumber";
import {
  buildOtisFormFieldKey,
  buildOtisOrgExpansionKey,
  buildOtisAccountKey,
  extractOtisFieldFromFormKey,
  findOrganization,
  findAccount,
} from "../../../utils/otisCombinationUtils";

// Custom Loader Component for SelectList
const CustomSelectLoader = () => (
  <div className="flex items-center justify-center pr-2">
    <svg
      className="animate-spin h-5 w-5 text-gray-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  </div>
);

interface EditedField {
  repId: string;
  orgId: string;
  accountId: string;
  field: string;
  value: string;
  timestamp: string;
}

type OTISField =
  | keyof typeof OTISRecords
  | "sales_notes"
  | "status";

const GRID_OTIS_FIELDS = (Object.keys(OTISRecords).slice(4) as OTISField[]).filter(
  (field) => !HIDDEN_GRID_COLUMNS.includes(field as (typeof HIDDEN_GRID_COLUMNS)[number])
);

const TableRow: React.FC<TableRowProps> = ({
  record,
  index,
  formData,
  handleFieldChange,
  selectKey,
  repCodeSearchOptions,
  organizationSearchOptions,
  accountSearchOptions,
  searchTerm,
  setSearchTerm,
  setFormData,
  currentPage,
  setCurrentPage,
  selectedRepIDS,
  selectedOrgIDs,
  selectedAccountIDs,
  fetchCustomerAccounts,
  dispatch,
  setSelectedRepId,
  setSelectedOrgId,
  setSelectedAccountId,
  repcodeRef,
  OrgRef,
  selectRef,
  shouldClear,
  setGrandTotal,
  tableRef,
}) => {
  const [expandedReps, setExpandedReps] = useState<Set<string>>(new Set());
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [clickedAccountId, setClickedAccountId] = useState<string | null>(null);
  const [editedFields, setEditedFields] = useState<Record<string, EditedField>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const ClearCompState = useSelector((state: RootState) => state.OTISReducer.ClearCompState);
  const addcancelData = useSelector((state: RootState) => state.OTISReducer.addrowcancelData);
  const selectedDistribution = useSelector(
    (state: RootState) => state.OTISReducer.selectedDistribution
  );
  const selectedOrganization = useSelector(
    (state: RootState) => state.OTISReducer.selectedOrganization
  );
  const userData = useSelector(user);

  const { control, setValue, watch } = useForm();

  const repCodeValue = watch("repCode");
  const organizationValue = watch("organization");
  const accountValue = watch("account");

  const areAllFieldsSelected = useMemo(() => {
    return !!repCodeValue && !!organizationValue && !!accountValue;
  }, [repCodeValue, organizationValue, accountValue]);

  const debouncedFetchCustomerAccounts = useMemo(
    () =>
      debounce(async (search, repId, orgId, accountId, page, dispatch) => {
        setIsLoading(true); // Start loader
        try {
          await fetchCustomerAccounts(search, repId, orgId, accountId, page, dispatch);
        } catch (error) {
          console.error("Error fetching customer accounts:", error);
        } finally {
          setIsLoading(false); // Stop loader
        }
      }, 300),
    [fetchCustomerAccounts, dispatch]
  );

  useEffect(() => {
    if (selectedDistribution && record.DISTRIBUTION_CHANNEL_NAME === selectedDistribution) {
      const newExpandedReps = new Set(expandedReps);
      newExpandedReps.add(record._id);
      setExpandedReps(newExpandedReps);

      const newExpandedAccounts = new Set(expandedAccounts);
      record.organizations?.forEach((org: any) => {
        if (org.accounts?.length > 1) {
          newExpandedAccounts.add(buildOtisOrgExpansionKey(org));
        }
      });
      setExpandedAccounts(newExpandedAccounts);
    } else {
      const newExpandedReps = new Set(expandedReps);
      newExpandedReps.delete(record._id);
      setExpandedReps(newExpandedReps);

      const newExpandedAccounts = new Set(expandedAccounts);
      record.organizations?.forEach((org: any) => {
        newExpandedAccounts.delete(buildOtisOrgExpansionKey(org));
      });
      setExpandedAccounts(newExpandedAccounts);
    }
  }, [selectedDistribution, record._id, record.DISTRIBUTION_CHANNEL_NAME, record.organizations]);

  useEffect(() => {
    if (selectedOrganization) {
      const matchingOrg = record.organizations?.find(
        (org: any) => org.orgName === selectedOrganization
      );

      if (matchingOrg) {
        const newExpandedReps = new Set(expandedReps);
        newExpandedReps.add(record._id);
        setExpandedReps(newExpandedReps);

        const newExpandedAccounts = new Set(expandedAccounts);
        if (matchingOrg.accounts?.length > 1) {
          newExpandedAccounts.add(buildOtisOrgExpansionKey(matchingOrg));
        }
        setExpandedAccounts(newExpandedAccounts);
      }
    } else {
      const newExpandedReps = new Set(expandedReps);
      newExpandedReps.delete(record._id);
      setExpandedReps(newExpandedReps);

      const newExpandedAccounts = new Set(expandedAccounts);
      record.organizations?.forEach((org: any) => {
        newExpandedAccounts.delete(buildOtisOrgExpansionKey(org));
      });
      setExpandedAccounts(newExpandedAccounts);
    }
  }, [selectedOrganization, record._id, record.organizations]);

  useEffect(() => {
    if (shouldClear || record?.isNew) {
      setValue("repCode", null);
      setValue("organization", null);
      setValue("account", null);
      const fieldsToClear = Object.keys(OTISRecords).slice(4) as OTISField[];
      fieldsToClear.forEach((field) => {
        const fieldKey = `${record._id}---${field}`;
        setValue(fieldKey, "");
      });
      setValue(`${record._id}---pub_goal`, "");
      setValue(`${record._id}---currEst`, "");
      setSelectedRepId(null);
      setSelectedAccountId(null);
      setSelectedOrgId(null);
      setEditedFields({});
      setFieldValues({});
      setFormData((prev: any) => ({
        ...prev,
        [`${record._id}---compInit`]: "",
      }));
      debouncedFetchCustomerAccounts("", "", "", "", 1, dispatch);
    }
  }, [shouldClear, record?._id, record?.isNew, setValue, dispatch, debouncedFetchCustomerAccounts]);

  useEffect(() => {
    if (addcancelData || record?.isNew) {
      setValue("repCode", null);
      setValue("organization", null);
      setValue("account", null);
      Object.keys(OTISRecords).slice(4).forEach((field) => {
        const fieldKey = `${record._id}---${field}`;
        setValue(fieldKey, "");
      });
      setSelectedRepId(null);
      setSelectedAccountId(null);
      setSelectedOrgId(null);
      setEditedFields({});
      setFieldValues({});
      debouncedFetchCustomerAccounts("", "", "", "", 1, dispatch);
    }
  }, [addcancelData, record?._id, record?.isNew, setValue, dispatch, debouncedFetchCustomerAccounts]);

  useEffect(() => {
    const handleScroll = () => {
      if (!record?.isNew) {
        [selectRef, repcodeRef, OrgRef].forEach((ref) => {
          if (ref.current) ref.current.blur();
        });
      }
    };
    const tableElement = tableRef.current;
    if (tableElement) tableElement.addEventListener("scroll", handleScroll);
    return () => {
      if (tableElement) tableElement.removeEventListener("scroll", handleScroll);
    };
  }, [selectRef, repcodeRef, OrgRef, tableRef, record?.isNew]);

  const handleFieldEdit = (
    repId: string,
    orgId: string,
    accountId: string,
    field: OTISField,
    value: string,
    orgName?: string | null,
    accountName?: string | null
  ) => {
    const fieldKey = buildOtisFormFieldKey({
      recordId: repId,
      orgId: orgId || null,
      orgName,
      accountId: accountId || null,
      accountName,
      field,
      isMainRow: !orgId && !accountId,
      isOrgLevelRow: !!orgId && !accountId,
    });
    setEditedFields((prev) => ({
      ...prev,
      [fieldKey]: {
        repId,
        orgId,
        accountId,
        field,
        value,
        timestamp: new Date().toISOString(),
      },
    }));

    setFieldValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));

    if (handleFieldChange) {
      handleFieldChange(
        repId,
        orgId,
        accountId,
        field,
        value,
        orgName,
        accountName
      );
    }
  };

 
 

  const unfilteredRecord = record;

  const getAccountFieldKey = (org: any, account: any, field: string) =>
    buildOtisFormFieldKey({
      recordId: record._id,
      orgId: org?.organizationId,
      orgName: org?.orgName,
      accountId: account?.accountNumber,
      accountName: account?.accountName,
      field,
    });

  const getOrgFieldKey = (org: any, field: string) =>
    buildOtisFormFieldKey({
      recordId: record._id,
      orgId: org?.organizationId,
      orgName: org?.orgName,
      field,
      isOrgLevelRow: true,
    });

  const hasOrgIdentity = (org: any) =>
    !!(
      String(org?.organizationId ?? "").trim() ||
      String(org?.orgName ?? "").trim()
    );

  /** Show org row when it has named accounts or org-level upload data (e.g. "All Accounts" rows with empty accounts[]). */
  const shouldShowOrganizationRow = (org: any, validAccounts: any[]) =>
    validAccounts.length > 0 || hasOrgIdentity(org);

  const getOrgLevelNumeric = (org: any, orgField: string) => {
    const raw = org[orgField]?.$numberDecimal ?? org[orgField] ?? "";
    const n = parseFloat(String(raw));
    return Number.isFinite(n) ? n : 0;
  };

  const totalCompGross = useMemo(() => {
    let hasExplicitZero = false;
    const total = unfilteredRecord.organizations?.reduce((total: number, org: any) => {
      return (
        total +
        (org.accounts?.reduce((acc: number, account: any) => {
          const value = formData[getAccountFieldKey(org, account, "compGross")] ||
                       account.compGross?.$numberDecimal || account.compGross || "";
          if (value === "0") hasExplicitZero = true;
          return acc + (value === "" ? 0 : parseFloat(value) || 0);
        }, 0) || 0)
      );
    }, 0);
    return total === 0 && !hasExplicitZero ? "" : total;
  }, [unfilteredRecord.organizations, formData]);

  const totalCompInit = useMemo(() => {
    let hasExplicitZero = false;
    const total = unfilteredRecord.organizations?.reduce((total: number, org: any) => {
      return (
        total +
        (org.accounts?.reduce((acc: number, account: any) => {
          const value = formData[getAccountFieldKey(org, account, "compInit")] ||
                       account.compInit?.$numberDecimal || account.compInit || "";
          if (value === "0") hasExplicitZero = true;
          return acc + (value === "" ? 0 : parseFloat(value) || 0);
        }, 0) || 0)
      );
    }, 0);
    return total === 0 && !hasExplicitZero ? "" : total;
  }, [unfilteredRecord.organizations, formData, record._id]);

  const totalPubGoal = useMemo(() => {
    const total = unfilteredRecord.organizations?.reduce((total: number, org: any) => {
      const namedAccounts = (org.accounts || []).filter((a: any) => a?.accountName);
      if (namedAccounts.length > 0) {
        return (
          total +
          (namedAccounts.reduce((acc: number, account: any) => {
            const fieldKey = getAccountFieldKey(org, account, "pub_goal");
            const value = formData[fieldKey] || account.pub_goal?.$numberDecimal || "";
            return acc + (value === "" ? 0 : parseFloat(value) || 0);
          }, 0) || 0)
        );
      }
      const fieldKey = getOrgFieldKey(org, "pub_goal");
      const value = formData[fieldKey] ?? getOrgLevelNumeric(org, "org_pub_goal");
      return total + (value === "" ? 0 : parseFloat(String(value)) || 0);
    }, 0);
    return total === 0 ? "0" : total;
  }, [unfilteredRecord.organizations, formData, record._id]);

  const totalOrgUnits = useMemo(() => {
    return record.organizations?.reduce((total: number, org: any) => {
      return (
        total +
        (org.accounts?.reduce((acc: number, account: any) => {
          return acc + (parseInt(account.units) || 0);
        }, 0) || 0)
      );
    }, 0);
  }, [record.organizations]);
  const totalCurrEst = useMemo(() => {
    const total = unfilteredRecord.organizations?.reduce((total: number, org: any) => {
      const namedAccounts = (org.accounts || []).filter((a: any) => a?.accountName);
      if (namedAccounts.length > 0) {
        return (
          total +
          (namedAccounts.reduce((acc: number, account: any) => {
            const fieldKey = getAccountFieldKey(org, account, "currEst");
            const value = formData[fieldKey] || account.currEst?.$numberDecimal || "";
            return acc + (value === "" ? 0 : parseFloat(value) || 0);
          }, 0) || 0)
        );
      }
      const fieldKey = getOrgFieldKey(org, "currEst");
      const value = formData[fieldKey] ?? getOrgLevelNumeric(org, "org_current_estimate");
      return total + (value === "" ? 0 : parseFloat(String(value)) || 0);
    }, 0);
    return total === 0 ? "0" : total;
  }, [unfilteredRecord.organizations, formData, record._id]);

  const totalSuppEst = useMemo(() => {
    const total = unfilteredRecord.organizations?.reduce((total: number, org: any) => {
      const namedAccounts = (org.accounts || []).filter((a: any) => a?.accountName);
      if (namedAccounts.length > 0) {
        return (
          total +
          (namedAccounts.reduce((acc: number, account: any) => {
            const fieldKey = getAccountFieldKey(org, account, "suppEst");
            const value = formData[fieldKey] || account.suppEst?.$numberDecimal || "";
            return acc + (value === "" ? 0 : parseFloat(value) || 0);
          }, 0) || 0)
        );
      }
      const fieldKey = getOrgFieldKey(org, "suppEst");
      const value = formData[fieldKey] ?? getOrgLevelNumeric(org, "org_supplemental_estimate");
      return total + (value === "" ? 0 : parseFloat(String(value)) || 0);
    }, 0);
    return total === 0 ? "0" : total;
  }, [unfilteredRecord.organizations, formData, record._id]);

  const totalCompNet = useMemo(() => {
    let hasExplicitZero = false;
    const total = unfilteredRecord.organizations?.reduce((total: number, org: any) => {
      return (
        total +
        (org.accounts?.reduce((acc: number, account: any) => {
          const value = formData[getAccountFieldKey(org, account, "compNet")] ||
                       account.compNet?.$numberDecimal || account.compNet || "";
          if (value === "0") hasExplicitZero = true;
          return acc + (value === "" ? 0 : parseFloat(value) || 0);
        }, 0) || 0)
      );
    }, 0);
    return total === 0 && !hasExplicitZero ? "" : total;
  }, [unfilteredRecord.organizations, formData]);

  const totalCpos_LTD = useMemo(() => {
    let hasExplicitZero = false;
    const total = unfilteredRecord.organizations?.reduce((total: number, org: any) => {
      return (
        total +
        (org.accounts?.reduce((acc: number, account: any) => {
          const value = formData[getAccountFieldKey(org, account, "Cpos_LTD")] ||
                       account.Cpos_LTD?.$numberDecimal || account.Cpos_LTD || "";
          if (value === "0") hasExplicitZero = true;
          return acc + (value === "" ? 0 : parseFloat(value) || 0);
        }, 0) || 0)
      );
    }, 0);
    return total === 0 && !hasExplicitZero ? "" : total;
  }, [unfilteredRecord.organizations, formData, record._id]);

  useEffect(() => {
    if (record && !record?.isNew) {
      const mainPubGoalKey = `${record._id}---pub_goal`;
      const mainCurrEstKey = `${record._id}---currEst`;
      const mainSalesNotesKey = `${record._id}---sales_notes`;
      const mainCompGrossKey = `${record._id}---compGross`;
      const mainCompNetKey = `${record._id}---compNet`;
      const mainPrintMeetingDateKey = `${record._id}---printmtdDate`;
      const mainSupplementalEstimateKey = `${record._id}---suppEst`;
      const mainCompInitKey = `${record._id}---compInit`;
      const mainCpos_LTDKey = `${record._id}---Cpos_LTD`;
      setValue(mainPubGoalKey, record.main_pubgoal || "");
      setValue(mainCurrEstKey, record.main_current_estimate || "");
      setValue(mainSalesNotesKey, record.main_sales_notes || "");
      setValue(mainCompGrossKey, totalCompGross);
      setValue(mainCompNetKey, totalCompNet);
      setValue(mainPrintMeetingDateKey, record.main_print_meeting_date || "");
      setValue(mainSupplementalEstimateKey, record.main_supplemental_estimate || "");
      setValue(mainCompInitKey, totalCompInit);
      setValue(mainCpos_LTDKey, totalCpos_LTD);
      record.organizations?.forEach((org: any) => {
        ["pub_goal", "currEst", "suppEst"].forEach((field) => {
          const orgField =
            field === "pub_goal"
              ? "org_pub_goal"
              : field === "currEst"
              ? "org_current_estimate"
              : "org_supplemental_estimate";
          const rawValue = org[orgField]?.$numberDecimal ?? org[orgField] ?? "";
          const value =
            rawValue === "" || isNaN(parseFloat(rawValue)) || parseFloat(rawValue) === 0
              ? ""
              : rawValue;
          const key = getOrgFieldKey(org, field);
          setValue(key, value);
        });
        const orgSalesNotesKey = getOrgFieldKey(org, "sales_notes");
        setValue(orgSalesNotesKey, org.org_sales_notes || "");
        org.accounts?.forEach((account: any) => {
          const fields = Object.keys(OTISRecords).slice(4) as OTISField[];
          fields.forEach((field) => {
            const key = getAccountFieldKey(org, account, field);
            const rawValue = account[field]?.$numberDecimal || account[field] || "";
            const value =
              field === "sales_notes"
                ? rawValue
                : (field === "compInit" || field === "compGross" || field === "compNet" || field === "Cpos_LTD") && rawValue === "0"
                ? "0"
                : rawValue === "" || isNaN(parseFloat(rawValue)) || parseFloat(rawValue) === 0
                ? ""
                : rawValue;
            setValue(key, value);
          });
        });
      });
    }
  }, [record?._id, record, setValue]);

  const toggleRepExpansion = (repId: string) => {
    const newSet = new Set(expandedReps);
    if (newSet.has(repId)) {
      newSet.delete(repId);
      const newExpandedAccounts = new Set(expandedAccounts);
      record.organizations?.forEach((org: any) => {
        newExpandedAccounts.delete(buildOtisOrgExpansionKey(org));
      });
      setExpandedAccounts(newExpandedAccounts);
    } else {
      newSet.add(repId);
      const newExpandedAccounts = new Set(expandedAccounts);
      record.organizations?.forEach((org: any) => {
        if (org.accounts?.length > 1) {
          newExpandedAccounts.add(buildOtisOrgExpansionKey(org));
        }
      });
      setExpandedAccounts(newExpandedAccounts);
    }
    setExpandedReps(newSet);
  };

  const toggleOrgAccountsExpansion = (orgExpansionKey: string) => {
    const distRaw = (record?.DISTRIBUTION_CHANNEL ?? record?.DISTRIBUTION_CHANNEL_NAME ?? "").toString();
    const dist = distRaw.trim().toUpperCase();
    const allowOrgAccountExpand = dist === "MASSMER" || dist === "RETINDEP";
    if (!allowOrgAccountExpand) return;

    const newExpandedAccounts = new Set(expandedAccounts);
    if (newExpandedAccounts.has(orgExpansionKey)) {
      newExpandedAccounts.delete(orgExpansionKey);
    } else {
      newExpandedAccounts.add(orgExpansionKey);
    }
    setExpandedAccounts(newExpandedAccounts);
  };

  const calculateGrandTotals = () => {
    const totals: Record<string, number> = {};
    Object.keys(OTISRecords)
      .slice(4)
      .forEach((field) => {
        if (field !== "sales_notes") {
          totals[field] = 0;
        }
      });
    Object.keys(formData).forEach((key) => {
      const field = extractOtisFieldFromFormKey(key) as OTISField;
      if (field && Object.keys(OTISRecords).slice(4).includes(field) && formData[key] !== "") {
        const value = field === "sales_notes" ? 0 : parseFloat(formData[key]) || 0;
        totals[field] += value;
      }
    });
    return totals;
  };

  useEffect(() => {
    const grandTotals = calculateGrandTotals();
    setGrandTotal(grandTotals);
  }, [formData, setGrandTotal]);

  const handleRowClick = (
    rowType: "main" | "org" | "account",
    id: string,
    data?: any
  ) => {
    if (rowType === "account") {
      setClickedAccountId(id);
    }
  };

  const calculateOrgUnits = (org: any) => {
    return org.accounts?.reduce((total: number, account: any) => {
      return total + (parseInt(account.units) || 0);
    }, 0);
  };

  const calculateOrgLtdUnits = (org: any) => {
    return org.accounts?.reduce((total: number, account: any) => {
      return total + (Number(account.ltdUnits) || 0);
    }, 0) || 0;
  };

  const calculateTotalFieldValue = (field: OTISField) => {
    if (field === "ltdUnits") {
      return record.organizations?.reduce((total: number, org: any) => {
        return total + calculateOrgLtdUnits(org);
      }, 0) || 0;
    }
    let total = 0;
    let hasExplicitZero = false;
    record.organizations?.forEach((org: any) => {
      org.accounts?.forEach((account: any) => {
        const fieldKey = getAccountFieldKey(org, account, field);
        const value = formData[fieldKey] || account[field]?.$numberDecimal || account[field] || "";
        if (value === "0") hasExplicitZero = true;
        total += (field === "sales_notes" || value === "") ? 0 : parseFloat(value) || 0;
      });
    });
    return total === 0 && !hasExplicitZero && (field === "compInit" || field === "compGross" || field === "compNet" || field === "Cpos_LTD") ? "" : total || "";
  };

  const calculateOrgFieldValue = (org: any, field: OTISField) => {
    if (field === "ltdUnits") {
      return calculateOrgLtdUnits(org);
    }
    let total = 0;
    let hasExplicitZero = false;
    org.accounts?.forEach((account: any) => {
      const fieldKey = getAccountFieldKey(org, account, field);
      const value = formData[fieldKey] || account[field]?.$numberDecimal || account[field] || "";
      if (value === "0") hasExplicitZero = true;
      total += (field === "sales_notes" || value === "") ? 0 : parseFloat(value) || 0;
    });
    return total === 0 && !hasExplicitZero && (field === "compInit" || field === "compGross" || field === "compNet" || field === "Cpos_LTD") ? "" : total || "";
  };

  const sumOrgCompField = (org: any, field: OTISField): number =>
    (org?.accounts ?? []).reduce((sum: number, account: any) => {
      const val = account?.[field];
      const raw = val != null && typeof val === "object" ? val.$numberDecimal : val;
      const n = Number(raw);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);

  const eanSelectedValue = useSelector(
    (state: RootState) => state.OTISReducer.data.eanSelectedValue
  );

  const ShowInputField = (
    record: any,
    orgId: string,
    accountId: string,
    field: OTISField,
    isMainRow: boolean = false,
    isOrgRow: boolean = false,
    org: any = null,
    isRepExpanded?: boolean,
    isAccountExpanded?: boolean,
    isOrgLevelRow: boolean = false,
    account: any = null
  ) => {
    const orgNameForKey = org?.orgName ?? null;
    const accountNameForKey = account?.accountName ?? null;
    const name = buildOtisFormFieldKey({
      recordId: record._id,
      orgId: orgId || null,
      orgName: orgNameForKey,
      accountId: accountId || null,
      accountName: accountNameForKey,
      field,
      isMainRow,
      isOrgLevelRow,
    });
    const isCollapsedView =
      field !== "sales_notes" &&
      field !== "compInit" &&
      field !== "compGross" &&
      field !== "compNet" &&
      field !== "retPercentage" &&
      field !== "cpos4wkPOS" &&
      field !== "cpos8wkPOS" &&
      field !== "Cpos_LTD" &&
      field !== "ltdUnits" &&
      ((isMainRow && !isRepExpanded && record.organizations?.length > 1) ||
        (isOrgRow && !isAccountExpanded && org?.accounts?.length > 1));
    const isEditableInCollapsedView =
      field === "pub_goal" ||
      field === "currEst" ||
      field === "suppEst";
    const isNewRow = record?.isNew;

    const getDisplayValue = (formValue: string | undefined) => {
      if (isNewRow) {
        return formData[name] || "";
      }
      if (formValue !== undefined && formValue !== "") {
        const value = typeof formValue === "object" ? "" : String(formValue);
        const numValue = parseFloat(value);
        if ((field === "compInit" || field === "compGross" || field === "compNet" || field === "Cpos_LTD") && value === "0") {
          return "0";
        }
        if (
          (field === "pub_goal" ||
           field === "currEst" ||
           field === "suppEst") &&
          numValue === 0
        ) {
          return "";
        }
        return value;
      }
      if (isMainRow && field === "pub_goal") {
        const value = record.main_pubgoal || "";
        return parseFloat(value) === 0 ? "" : value;
      }
      if (isMainRow && field === "sales_notes") {
        return record.main_sales_notes || "";
      }
      if (isMainRow && field === "currEst") {
        const value = record.main_current_estimate || "";
        return parseFloat(value) === 0 ? "" : value;
      }
      // Print Meeting Date column has been removed from the grid; no main-row handler needed here.
      if (isMainRow && field === "suppEst") {
        const value = record.main_supplemental_estimate || "";
        return parseFloat(value) === 0 ? "" : value;
      }
      if (isOrgLevelRow && org && (field === "pub_goal" || field === "currEst" || field === "suppEst")) {
        const orgField =
          field === "pub_goal"
            ? "org_pub_goal"
            : field === "currEst"
            ? "org_current_estimate"
            : "org_supplemental_estimate";
        const value = org[orgField]?.$numberDecimal ?? org[orgField] ?? "";
        return parseFloat(value) === 0 ? "" : value;
      }
      if (isOrgLevelRow && org && field === "sales_notes") {
        return org.org_sales_notes || "";
      }
      if (orgId && accountId) {
        const organization = findOrganization(
          record.organizations,
          orgId,
          orgNameForKey
        );
        const accountMatch = findAccount(
          organization?.accounts,
          accountId,
          accountNameForKey
        );
        const account = accountMatch;
        if (account) {
          if (field === "sales_notes") {
            return account[field] || "";
          }
          if (account[field] !== undefined) {
            const rawValue = account[field]?.$numberDecimal || account[field] || "";
            const value = typeof rawValue === "object" ? "" : String(rawValue);
            if ((field === "compInit" || field === "compGross" || field === "compNet" || field === "Cpos_LTD") && value === "0") {
              return "";
            }
            return value === "" || isNaN(parseFloat(value)) || parseFloat(value) === 0 ? "" : value;
          }
        }
      }
      return "";
    };

    const handleValueChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      field: OTISField
    ) => {
      if (isNewRow && !areAllFieldsSelected) {
        toast.error(
          "Please select all three - Rep Code, Updating Organization and Account Name",
          { toastId: "uniqueid" }
        );
        return;
      }
      let value = e.target.value;

      if (isNewRow && field !== "sales_notes") {
        const numValue = parseFloat(value);
        if (numValue <= 0 && value !== "") {
          toast.error("Value must be greater than 0", { toastId: "uniqueid" });
          return;
        }
      }

      const effectiveAccountId = isOrgLevelRow ? "" : accountId;
      if (field !== "sales_notes") {
        if (value === "") {
          setValue(name, "");
          handleFieldEdit(
            record._id,
            orgId,
            effectiveAccountId,
            field,
            "0",
            orgNameForKey,
            accountNameForKey
          );
        } else {
          const cleanedValue = value.replace(/[^0-9]/g, "");
          if (cleanedValue === "") {
            setValue(name, "");
            handleFieldEdit(
              record._id,
              orgId,
              effectiveAccountId,
              field,
              "0",
              orgNameForKey,
              accountNameForKey
            );
          } else {
            setValue(name, cleanedValue);
            handleFieldEdit(
              record._id,
              orgId,
              effectiveAccountId,
              field,
              cleanedValue,
              orgNameForKey,
              accountNameForKey
            );
          }
        }
      } else {
        setValue(name, value);
        handleFieldEdit(
          record._id,
          orgId,
          effectiveAccountId,
          field,
          value,
          orgNameForKey,
          accountNameForKey
        );
      }
    };

    if (field === "ltdUnits") {
      let value = 0;
      if (isMainRow) {
        value = record.organizations?.reduce((total: number, org: any) => total + calculateOrgLtdUnits(org), 0) || 0;
      } else if (isOrgLevelRow && org) {
        value = calculateOrgLtdUnits(org);
      } else if (accountId && account) {
        value = Number(account.ltdUnits) || 0;
      } else if (org && org.accounts) {
        value = calculateOrgLtdUnits(org);
      }

      return (
        <td
          key={name}
          className="text-center borderInputs bg-white min-w-[100px]"
          style={{
            position: "sticky",
            left: "var(--frz-l4)",
            zIndex: 3,
            backgroundColor: "#fff",
            borderRight: "1px solid #ccc",
            borderBottom: "1px solid #ccc",
          }}
        >
          {formatNumber(value) || "0"}
        </td>
      );
    }

    if (field === "sales_notes") {
      return (
        <td
          key={name}
          className="text-center borderInputs border border-gray-300"
        >
          <Controller
            control={control}
            name={name}
            defaultValue={getDisplayValue(formData[name])}
            render={({ field: controllerField }) => (
              <input
                {...controllerField}
                value={controllerField.value || ""}
                onChange={(e) => {
                  controllerField.onChange(e.target.value);
                  handleValueChange(e, field);
                }}
                className={`${getInputClassName(field)} ${editedFields[name] ? "border-2 border-blue-500" : ""
                  } ${((isMainRow || isOrgRow) && !record?.isNew && field !== "sales_notes")
                  ? "cursor-not-allowed"
                  : "cursor-text"
                }`}
                placeholder="Enter notes..."
                disabled={
                  isLoading || // Disable when loading
                  ((isMainRow || isOrgRow) && !record?.isNew && field !== "sales_notes") ||
                  (isNewRow && !areAllFieldsSelected) ||  userData?.role?.includes("viewer")
                }
                
              />
            )}
          />
        </td>
      );
    }

    if (isNewRow) {
      return (
        <td
          key={name}
          className={`text-center borderInputs border border-gray-300 ${field === "pub_goal" ? "hidden" : ""}`}
        >
          <Controller
            control={control}
            name={name}
            defaultValue=""
            render={({ field: controllerField }) => (
              <input
                type="text"
                {...controllerField}
                value={controllerField.value || ""}
                onChange={(e) => handleValueChange(e, field)}
                className={`${getInputClassName(field)} ${
                  editedFields[name] ? "border-2 border-blue-500" : ""
                }`}
                disabled={
                  isLoading || // Disable when loading
                  field === "compInit" ||
                  field === "compGross" ||
                  field === "compNet" ||
                  field === "retPercentage" ||
                  field === "cpos4wkPOS" ||
                  field === "cpos8wkPOS" ||
                  field === "Cpos_LTD" ||
                  !areAllFieldsSelected || userData?.role?.includes("viewer")
                }
              />
            )}
          />
        </td>
      );
    }

    if (
      isOrgRow &&
      !accountId &&
      (field === "compInit" ||
        field === "compGross" ||
        field === "compNet" ||
        field === "Cpos_LTD")
    ) {
      return (
        <td
          key={name}
          className="text-center borderInputs border border-gray-300"
        >
          <input
            type="text"
            readOnly
            value={formatNumber(sumOrgCompField(org, field))}
            className="py-1 border border-gray-300 rounded-md focus:outline-none bg-gray-100 text-gray-700 w-[85%] cursor-not-allowed text-center"
          />
        </td>
      );
    }

    if (isCollapsedView && !isEditableInCollapsedView) {
      const totalValue = isMainRow
        ? calculateTotalFieldValue(field)
        : calculateOrgFieldValue(org, field);
      return (
        <td
          key={name}
          className="text-center borderInputs border border-gray-300"
        >
          <input
            type="text"
            readOnly
            value={totalValue === "" ? "" : totalValue}
            className="py-1 border border-gray-300 rounded-md focus:outline-none bg-gray-100 text-gray-700 w-[85%] cursor-not-allowed"
          />
        </td>
      );
    }

    if (
      isMainRow &&
      (field === "pub_goal" ||
        field === "currEst" ||
        field === "suppEst")
    ) {
      const totalValue =
        field === "pub_goal"
          ? totalPubGoal
          : field === "currEst"
          ? totalCurrEst
          : field === "suppEst"
          ? totalSuppEst
          : ""
      return (
        <td
          key={name}
          className={`text-center relative justify-center item-center borderInputs border border-gray-300 py-6 ${field === "pub_goal" ? "hidden" : ""}`}
        >
          <div className="flex flex-col justify-center item-center pl-2">
            <Controller
              control={control}
              name={name}
              defaultValue={getDisplayValue(formData[name])}
              render={({ field: controllerField }) => {
                const displayValue =
                  controllerField.value &&
                  parseFloat(controllerField.value) === 0
                    ? ""
                    : controllerField.value || "";
                return (
                  <input
                    type="text"
                    {...controllerField}
                    value={displayValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      const newValue = value === "" ? "0" : value;
                      controllerField.onChange(newValue);
                      handleValueChange(
                        { ...e, target: { ...e.target, value: newValue } },
                        field
                      );
                    }}
                    className={`${getInputClassName(field)} ${
                      editedFields[name] ? "border-2 border-blue-500" : "text-black"
                    } cursor-text px-[0.3] flex justify-center item-center`}
                    placeholder=""
                    disabled={isLoading ||  userData?.role?.includes("viewer")} // Disable when loading
                  />
                );
              }}
            />
          </div>
        </td>
      );
    }

    if (
      isOrgLevelRow &&
      org &&
      (field === "pub_goal" || field === "currEst" || field === "suppEst")
    ) {
      const totalValue =
        field === "pub_goal"
          ? calculateOrgFieldValue(org, "pub_goal")
          : field === "currEst"
          ? calculateOrgFieldValue(org, "currEst")
          : calculateOrgFieldValue(org, "suppEst");
      return (
        <td
          key={name}
          className={`text-center relative justify-center item-center borderInputs border border-gray-300 py-6 ${field === "pub_goal" ? "hidden" : ""}`}
        >
          <div className="flex flex-col justify-center item-center pl-2">
            <Controller
              control={control}
              name={name}
              defaultValue={getDisplayValue(formData[name])}
              render={({ field: controllerField }) => {
                const displayValue =
                  controllerField.value &&
                  parseFloat(controllerField.value) === 0
                    ? ""
                    : controllerField.value || "";
                return (
                  <input
                    type="text"
                    {...controllerField}
                    value={displayValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      const newValue = value === "" ? "0" : value;
                      controllerField.onChange(newValue);
                      handleValueChange(
                        { ...e, target: { ...e.target, value: newValue } },
                        field
                      );
                    }}
                    className={`${getInputClassName(field)} ${
                      editedFields[name] ? "border-2 border-blue-500" : "text-black"
                    } cursor-text px-[0.3] flex justify-center item-center`}
                    placeholder=""
                    disabled={isLoading || userData?.role?.includes("viewer")}
                  />
                );
              }}
            />
          </div>
        </td>
      );
    }

    const initialDisplayValue = getDisplayValue(formData[name]);
    return (
      <td
        key={name}
        className={`text-center borderInputs border px-1 border-gray-300 ${field === "pub_goal" ? "hidden" : ""}`}
      >
        <Controller
          control={control}
          name={name}
          defaultValue={initialDisplayValue}
          render={({ field: controllerField }) => (
            <input
              type="text"
              {...controllerField}
              value={
                field === "compInit" ||
                field === "compGross" ||
                field === "compNet" ||
                field === "retPercentage" ||
                field === "cpos4wkPOS" ||
                field === "cpos8wkPOS" ||
                field === "Cpos_LTD"
                  ? formatNumber(controllerField.value) || "0"
                  : ClearCompState &&
                    Number(controllerField.value) === 0 &&
                    !eanSelectedValue
                  ? ""
                  : controllerField.value || ""
              }
              onChange={(e) => {
                const value = e.target.value;
                controllerField.onChange(value);
                handleValueChange(e, field);
              }}
              className={`${getInputClassName(field)} ${
                editedFields[name] ? "border-2 border-blue-500 " : "text-black"
              } ${
                (isMainRow &&
                  !record?.isNew &&
                  !["pub_goal", "currEst", "printmtdDate", "suppEst"].includes(
                    field
                  )) ||
                field === "compInit" ||
                field === "compGross" ||
                field === "compNet" ||
                field === "retPercentage" ||
                field === "cpos4wkPOS" ||
                field === "cpos8wkPOS" ||
                field === "Cpos_LTD"
                  ? "cursor-not-allowed bg-gray-100"
                  : "cursor-text"
              } ${
                field === "compInit" ||
                field === "compGross" ||
                field === "compNet" ||
                field === "retPercentage" ||
                field === "cpos4wkPOS" ||
                field === "cpos8wkPOS" ||
                field === "Cpos_LTD"
                  ? "text-center"
                  : ""
              }`}
              disabled={
                isLoading || // Disable when loading
                (isMainRow &&
                  !record?.isNew &&
                  !["pub_goal", "currEst", "printmtdDate", "suppEst"].includes(
                    field
                  )) ||
                field === "compInit" ||
                field === "compGross" ||
                field === "compNet" ||
                field === "retPercentage" ||
                field === "cpos4wkPOS" ||
                field === "cpos8wkPOS" ||
                field === "Cpos_LTD" ||  userData?.role?.includes("viewer")
              }
            />
          )}
        />
      </td>
    );
  };

  const isRepExpanded = expandedReps.has(record._id);
  const isNew = record?.isNew;
  const shouldShowIcon = !record.organizations?.every((org: any) =>
    !org?.organizationId &&
    !org?.organizationName &&
    !org.accounts?.some((account: any) =>
      account?.accountName?.trim() || account?.accountNumber != null
    )
  );

  const findOrganizationByAccount = (accountId: string) => {
    return accountSearchOptions.find((account) => account.value === accountId)?.orgId;
  };

  const findDistributionByOrganization = (orgId: string) => {
    return organizationSearchOptions.find((org) => org.value === orgId)?.repId;
  };

  return (
    <>
      <tr
        key={`row-${index}`}
        id={`${record._id}`}
        className="bg-white text-sm"
      >
        <td className="text-center overflow-hidden text-ellipsis px-2 borderInputs borderInputs-first py-3 sticky bg-white z-[3] min-w-[150px]" style={{ left: "var(--frz-l1)", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>
          {!isNew ? (
            <div className="flex flex-col">
              <span>
                {record?.DISTRIBUTION_CHANNEL_NAME && record?.DISTRIBUTION_CHANNEL_NAME !== "NULL"
                  ? record.DISTRIBUTION_CHANNEL_NAME
                  : record?.DISTRIBUTION_CHANNEL || "Not Defined"}
              </span>
              {record?.DISTRIBUTION_CHANNEL && (
                <span>[{record.DISTRIBUTION_CHANNEL}]</span>
              )}
            </div>
          ) : (
            <div className="relative">
              {isNew && (
                <label className="absolute -top-5 left-0 text-red-500 text-sm">
                  <span className="text-red-500">*</span>
                </label>
              )}
              <Controller
                name="repCode"
                control={control}
                render={({ field }) => (
                  <SelectList
                    {...field}
                    ref={repcodeRef}
                    options={repCodeSearchOptions}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    isClearable
                    placeholder="Select Distribution Channel Name"
                    isLoading={isLoading}
                    isDisabled={isLoading} // Disable when loading
                    components={{ LoadingIndicator: CustomSelectLoader }}
                    onChange={(opt) => {
                      if (!opt) {
                        dispatch(setFinalData(null));
                        setCurrentPage(1);
                        dispatch(setInputtedData(false));
                        setFieldValues({});
                        [
                          "null---sales_notes",
                          "null---pub_goal",
                          "null---currEst",
                          "null---printmtdDate",
                          "null---suppEst",
                          "null---compInit",
                          "null---compGross",
                          "null---compNet",
                          "null---retPercentage",
                          "null---cpos4wkPOS",
                          "null---cpos8wkPOS",
                          "null---Cpos_LTD"
                        ].forEach(field => {
                          setValue(field, null);
                        });
                        field.onChange(null);
                        setSelectedRepId(null);
                        setCurrentPage(1);
                        debouncedFetchCustomerAccounts("", "", selectedOrgIDs?.value || "", selectedAccountIDs?.value || "", 1, dispatch);
                        return;
                      }
                      field.onChange(opt);
                      setSelectedRepId(opt);
                      debouncedFetchCustomerAccounts(
                        searchTerm,
                        opt.value,
                        selectedOrgIDs?.value || "",
                        selectedAccountIDs?.value || "",
                        1,
                        dispatch
                      );
                    }}
                    onInputChange={(val) => {
                      setSearchTerm(val);
                      return val;
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onMenuScrollToBottom={() => {
                      setCurrentPage(currentPage + 1);
                      dispatch(setIsScroll(true));
                    }}
                    menuPlacement="auto"
                    className={selectClassName}
                    styles={selectStyles}
                  />
                )}
              />
            </div>
          )}
        </td>
        <td className="overflow-hidden text-ellipsis myclass sticky bg-white z-[3] min-w-[150px] borderInputs" style={{ left: "var(--frz-l2)", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>
          {isNew ? (
            <div className="relative">
              {isNew && (
                <label className="absolute -top-5 left-0 text-red-500 text-sm">
                  <span className="text-red-500">*</span>
                </label>
              )}
              <Controller
                name="organization"
                control={control}
                render={({ field }) => (
                  <SelectList
                    {...field}
                    ref={OrgRef}
                    options={organizationSearchOptions}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                    isClearable
                    placeholder="Select Organization"
                    isLoading={isLoading}
                    isDisabled={isLoading} // Disable when loading
                    components={{ LoadingIndicator: CustomSelectLoader }}
                    onChange={(opt) => {
                      if (!opt) {
                        dispatch(setInputtedData(false));
                        setFieldValues({});
                        setCurrentPage(1);
                        dispatch(setFinalData(null));
                        [
                          "null---sales_notes",
                          "null---pub_goal",
                          "null---currEst",
                          "null---printmtdDate",
                          "null---suppEst",
                          "null---compInit",
                          "null---compGross",
                          "null---compNet",
                          "null---retPercentage",
                          "null---cpos4wkPOS",
                          "null---cpos8wkPOS",
                          "null---Cpos_LTD"
                        ].forEach(field => {
                          setValue(field, null);
                        });
                        field.onChange(null);
                        setSelectedOrgId(null);
                        setCurrentPage(1);
                        debouncedFetchCustomerAccounts(
                          "",
                          selectedRepIDS?.value || "",
                          "",
                          selectedAccountIDs?.value || "",
                          1,
                          dispatch
                        );
                        return;
                      }
                      field.onChange(opt);
                      setSelectedOrgId(opt);
                      setCurrentPage(1);
                      debouncedFetchCustomerAccounts(
                        searchTerm,
                        selectedRepIDS?.value || "",
                        opt.value,
                        selectedAccountIDs?.value || "",
                        1,
                        dispatch
                      );
                    }}
                    onInputChange={(val) => {
                      setSearchTerm(val);
                      return val;
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onMenuScrollToBottom={() => {
                      setCurrentPage(currentPage + 1);
                      dispatch(setIsScroll(true));
                    }}
                    menuPlacement="auto"
                    className={selectClassName}
                    styles={selectStyles}
                  />
                )}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              Total Organizations
              {shouldShowIcon && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRepExpansion(record._id);
                  }}
                  className="cursor-pointer select-none text-xl leading-none w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 transition"
                >
                  {isRepExpanded ? "−" : "+"}
                </span>
              )}
            </div>
          )}
        </td>
        <td className="hidden borderInputs overflow-hidden text-ellipsis px-2">
          {!isNew ? (
            <div className="flex items-center gap-2">Total Accounts</div>
          ) : (
            <div className="relative">
              {isNew && (
                <label className="absolute -top-5 left-0 text-red-500 text-sm">
                  <span className="text-red-500">*</span>
                </label>
              )}
              <Controller
                name="account"
                control={control}
                render={({ field }) => (
                  <SelectList
                    {...field}
                    ref={selectRef}
                    options={accountSearchOptions}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                    isClearable
                    placeholder="Select Account Name"
                    isLoading={isLoading}
                    isDisabled={isLoading} // Disable when loading
                    components={{ LoadingIndicator: CustomSelectLoader }}
                    onChange={(opt) => {
                      if (!opt) {
                        dispatch(setFinalData(null));
                        setCurrentPage(1);
                        dispatch(setInputtedData(false));
                        setFieldValues({});
                        [
                          "null---sales_notes",
                          "null---pub_goal",
                          "null---currEst",
                          "null---printmtdDate",
                          "null---suppEst",
                          "null---compInit",
                          "null---compGross",
                          "null---compNet",
                          "null---retPercentage",
                          "null---cpos4wkPOS",
                          "null---cpos8wkPOS",
                          "null---Cpos_LTD"
                        ].forEach(field => {
                          setValue(field, null);
                        });
                        field.onChange(null);
                        setCurrentPage(1);
                        setSelectedAccountId(null);
                        debouncedFetchCustomerAccounts(
                          "",
                          selectedRepIDS?.value || "",
                          selectedOrgIDs?.value || "",
                          "",
                          1,
                          dispatch
                        );
                        return;
                      }
                      field.onChange(opt);
                      setSelectedAccountId(opt);
                      debouncedFetchCustomerAccounts(
                        searchTerm,
                        selectedRepIDS?.value || "",
                        selectedOrgIDs?.value || "",
                        opt.value,
                        1,
                        dispatch
                      );
                    }}
                    onInputChange={(val) => {
                      setSearchTerm(val);
                      return val;
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onMenuScrollToBottom={() => {
                      setCurrentPage(currentPage + 1);
                      dispatch(setIsScroll(true));
                    }}
                    menuPlacement="auto"
                    className={selectClassName}
                    styles={selectStyles}
                  />
                )}
              />
            </div>
          )}
        </td>
        <td className="text-center borderInputs sticky bg-white z-[3] min-w-[100px]" style={{ left: "var(--frz-l3)", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>{formatNumber(totalOrgUnits) || "0"}</td>
        {GRID_OTIS_FIELDS.map((field) => (
          <React.Fragment key={field}>
            {ShowInputField(record, "", "", field, true, false, null, isRepExpanded)}
          </React.Fragment>
        ))}
      </tr>
      {isRepExpanded &&
        // record.organizations?.map((org: any, orgIndex: number) => {
        [...(record.organizations ?? [])]
 .sort((a: any, b: any) => {
   const aAll = String(a?.orgName ?? "").trim().toLowerCase() === "all other";
   const bAll = String(b?.orgName ?? "").trim().toLowerCase() === "all other";
   if (aAll && !bAll) return -1;
   if (!aAll && bAll) return 1;
   return 0;
 })
 .map((org: any, orgIndex: number) => {
          const validAccounts = (org.accounts || []).filter(
            (account: any) => account.accountName
          );
          if (!shouldShowOrganizationRow(org, validAccounts)) return null;

          const distRaw = (record?.DISTRIBUTION_CHANNEL ?? record?.DISTRIBUTION_CHANNEL_NAME ?? "").toString();
          const dist = distRaw.trim().toUpperCase();
          const allowOrgAccountExpand = dist === "MASSMER" || dist === "RETINDEP";

          const orgExpansionKey = buildOtisOrgExpansionKey(org);
          const isAccountExpanded = allowOrgAccountExpand && expandedAccounts.has(orgExpansionKey);
          const orgUnits = calculateOrgUnits(org);
          const showOrgExpandIcon = allowOrgAccountExpand && validAccounts.length >= 1;

          return (
            <React.Fragment key={orgExpansionKey}>
              {/* Org row: always shows "All Accounts" and org-level fields */}
              <tr
                className="myclass highlight"
                onClick={() => handleRowClick("org", org.organizationId)}
                style={{ cursor: "pointer" }}
              >
                <td className="text-center borderInputs borderInputs-first sticky bg-white z-[3] min-w-[150px]" style={{ left: "var(--frz-l1)", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}></td>
                <td className="overflow-hidden text-ellipsis myclass sticky bg-white z-[3] min-w-[150px] borderInputs" style={{ left: "var(--frz-l2)", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>
                  <div className="flex items-center gap-1 min-w-0">
                    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                      <span className="truncate">{org.orgName}</span>
                      <span className="truncate text-gray-500">[{org.organizationId}]</span>
                    </div>
                    {showOrgExpandIcon && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOrgAccountsExpansion(orgExpansionKey);
                        }}
                        className="cursor-pointer select-none text-xl leading-none w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-gray-200 transition"
                      >
                        {isAccountExpanded ? "−" : "+"}
                      </span>
                    )}
                  </div>
                </td>
                <td className="hidden overflow-hidden text-ellipsis myclass">
                  <div className="flex items-center gap-2">All Accounts</div>
                </td>
                <td className="text-center borderInputs sticky bg-white z-[3] min-w-[100px]" style={{ left: "var(--frz-l3)", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>{formatNumber(orgUnits) || "0"}</td>
                {GRID_OTIS_FIELDS.map((field) => (
                  <React.Fragment key={field}>
                    {ShowInputField(
                      record,
                      org.organizationId,
                      "",
                      field,
                      false,
                      true,
                      org,
                      isRepExpanded,
                      isAccountExpanded,
                      field === "pub_goal" ||
                        field === "currEst" ||
                        field === "suppEst" ||
                        field === "sales_notes"
                    )}
                  </React.Fragment>
                ))}
              </tr>
              {/* When expanded: all account rows below "All Accounts" */}
              {allowOrgAccountExpand &&
                isAccountExpanded &&
                validAccounts.map((account: any) => (
                  <tr
                    key={`${orgExpansionKey}-${buildOtisAccountKey(account.accountNumber, account.accountName)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick("account", account._id, account);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {/* <td className="borderInputs"></td>
                    <td className="borderInputs"></td>
                    <td className="indented myclass flex flex-col number2">
                      <span>{account.accountName}</span>
                      {account.accountNumber != null && (
                        <span style={{
                          visibility: String(account.accountNumber).length >= 3 ? "visible" : "hidden",
                        }}>
                          [{account.accountNumber}]
                        </span>
                      )}
                    </td> */}
                    <td className="borderInputs borderInputs-first sticky bg-white z-[3] min-w-[150px]" style={{ left: "var(--frz-l1)", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}></td>
<td className="myclass flex flex-col number2 sticky bg-white z-[3] min-w-[150px] overflow-hidden text-ellipsis borderInputs" style={{ left: "var(--frz-l2)", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>
<span>{account.accountName}</span>

  {account.accountNumber != null && (
<span style={{

      visibility: String(account.accountNumber).length >= 3 ? "visible" : "hidden",

    }}>

      [{account.accountNumber}]
</span>

  )}
</td>
<td className="hidden myclass">All Accounts</td>
 
                    <td className="text-center borderInputs sticky bg-white z-[3] min-w-[100px]" style={{ left: "var(--frz-l3)", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}>{formatNumber(account.units) || "0"}</td>
                    {GRID_OTIS_FIELDS.map((field) => (
                      <React.Fragment key={field}>
                        {ShowInputField(
                          record,
                          org.organizationId,
                          account.accountNumber,
                          field,
                          false,
                          false,
                          org,
                          isRepExpanded,
                          isAccountExpanded,
                          false,
                          account
                        )}
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
            </React.Fragment>
          );
        })}
    </>
  );
};

export default React.memo(TableRow);